import {
  ClientCreation,
  ClientCreationWithErrors,
  ClientRaw,
  ErrorClient,
  ErrorClientType,
  ProductCreation,
  ErrorUploadClient,
  ETitleClient,
} from '@Arti-zen/package-backoffice'
import { ApiArtizenBO } from 'utils/artizenConnector'

const errorProperties: Record<ErrorClientType, (keyof ClientCreation)[]> = {
  [ErrorClientType.NO_ID]: ['id'],
  [ErrorClientType.NAN_ID]: ['id'],
  [ErrorClientType.NO_LASTNAME]: ['lastName'],
  [ErrorClientType.UNCORRECT_PHONE]: ['phone'],
  [ErrorClientType.UNCCORECT_MOBILE]: ['mobile'],
  [ErrorClientType.UNCORRECT_EMAIL]: ['email'],
  [ErrorClientType.UNKNOWN_POSTAL_CODE_CITY]: ['postalCode', 'city'],
  [ErrorClientType.UNCORRECT_SIREN]: ['siren'],
  [ErrorClientType.NOT_PROFESSIONAL_AND_SIREN]: ['siren'],
  [ErrorClientType.PROFESSIONAL_AND_NO_SIREN]: ['siren'],
  [ErrorClientType.NO_IS_PROFESSIONAL]: ['isProfessional'],
  [ErrorClientType.UNCORRECT_IS_PROFESSIONAL]: ['isProfessional'],
  [ErrorClientType.UNCORRECT_TITLE]: ['title'],
  [ErrorClientType.PROFESSIONAL_AND_TITLE]: ['title'],
  [ErrorClientType.POSTAL_CODE_IN_ADDRESS]: ['postalCode', 'city'],

  [ErrorClientType.UNKNOWN_SYMBOL]: [],
  [ErrorClientType.CLIENT_NOT_FOUND]: [],
}

export const isErrorCorrected = (error: ErrorClient, corrections: Partial<ProductCreation>) => {
  const properties = errorProperties[error.type]
  return (
    properties.some((property) => property && property in corrections) ||
    (error.type === ErrorClientType.UNKNOWN_SYMBOL && Object.hasOwn(corrections, error.property))
  )
}

const getChunks = <T extends ClientCreation | ClientRaw>(clients: T[], defaultChunkSize: number): T[][] => {
  const sortedClients = [...clients].sort((a, b) => {
    const lastNameComparison = a.lastName.localeCompare(b.lastName)

    if (lastNameComparison !== 0) {
      return lastNameComparison
    }

    return (a.firstName ?? '').localeCompare(b.firstName ?? '')
  })

  const chunks: T[][] = []

  let start = 0

  while (start < sortedClients.length) {
    let end = Math.min(start + defaultChunkSize, sortedClients.length)

    while (
      end < sortedClients.length &&
      sortedClients[end].lastName === sortedClients[end - 1].lastName &&
      sortedClients[end].firstName === sortedClients[end - 1].firstName
    ) {
      end++
    }

    chunks.push(sortedClients.slice(start, end))
    start = end
  }

  return chunks
}

export const sendClientsCreationByChunk = async ({
  clientsToSend,
  companyId,
  onModify,
  onProgress,
}: {
  clientsToSend: ClientCreation[]
  companyId: string
  onModify: boolean
  onProgress: (processed: number) => void
}): Promise<{
  success: boolean
  csv?: string
  clients?: ClientCreation[]
  clientsWithErrors?: ClientCreationWithErrors[]
  error?: ErrorUploadClient
}> => {
  const clients: ClientCreation[] = []
  const clientsWithErrors: ClientCreationWithErrors[] = []
  const csvs: string[] = []
  let error: ErrorUploadClient = null

  const chunks = getChunks(clientsToSend, 100)

  let progress = 0

  for (const chunk of chunks) {
    const res = onModify
      ? await ApiArtizenBO.clients.updateClientsByCompany(companyId, chunk)
      : await ApiArtizenBO.clients.uploadClientsByCompany(companyId, chunk)

    if (res.error) error = res.error
    if (res.clients) clients.push(...res.clients)
    if (res.clientsWithErrors) clientsWithErrors.push(...res.clientsWithErrors)
    if (res.success && res.csv) csvs.push(res.csv)

    progress += chunk.length
    onProgress(Math.min(progress, clientsToSend.length))
  }

  const finalCSV = csvs
    .map((csv) => csv.trim())
    .filter(Boolean)
    .map((csv, index) => {
      const lines = csv.split(`\n`)
      return index === 0 ? lines : lines.slice(1)
    })
    .flat()
    .join(`\n`)

  return { success: !error, clients, clientsWithErrors, csv: finalCSV, error }
}

export const sendClientsRawByChunk = async ({
  clientsToSend,
  companyId,
  onModify,
}: {
  clientsToSend: ClientRaw[]
  companyId: string
  onModify: boolean
}): Promise<{
  success: boolean
  clients?: ClientCreation[]
  clientsWithErrors?: ClientCreationWithErrors[]
  error?: string
  duplicates?: ClientRaw[]
}> => {
  const clients: ClientCreation[] = []
  const clientsWithErrors: ClientCreationWithErrors[] = []
  const duplicates: ClientRaw[] = []

  const chunks = getChunks(clientsToSend, 200)

  for (const chunk of chunks) {
    // @ts-expect-error
    const result = await ApiArtizenBO.clients.previewClientsFromClientsRaw(companyId, chunk, onModify)

    if (!result.success) return result

    if (result.clients) clients.push(...result.clients)
    if (result.clientsWithErrors) clientsWithErrors.push(...result.clientsWithErrors)
    if (result.duplicates) duplicates.push(...result.duplicates)
  }

  return { success: true, clients, clientsWithErrors, duplicates }
}

export const removeErrors = (client: Partial<ClientCreationWithErrors>): ClientCreation => {
  const { errors: _, ...clientWithoutErrors } = client
  return clientWithoutErrors as ClientCreation
}

export const getCorrectedClient = (client: ClientCreationWithErrors, corrections: Partial<ClientCreation>) => {
  const correctionsWithoutErrors = removeErrors(corrections)

  const remainingErrors = { ...client.errors }

  for (const key in remainingErrors) {
    if (Object.hasOwn(corrections, key)) {
      delete remainingErrors[key]
    }
  }

  if (Object.hasOwn(correctionsWithoutErrors, 'isProfessional')) {
    const compareTo = { ...client, ...correctionsWithoutErrors }

    if (correctionsWithoutErrors.isProfessional === true) {
      if (compareTo.title) {
        remainingErrors['title'] = { type: ErrorClientType.PROFESSIONAL_AND_TITLE }
      }

      // if (!compareTo.siren) {
      //   remainingErrors.siren = { type: ErrorClientType.PROFESSIONAL_AND_NO_SIREN }
      // }
    } else if (compareTo.siren) {
      remainingErrors.siren = { type: ErrorClientType.NOT_PROFESSIONAL_AND_SIREN }
    }
  }

  return {
    correctedClient: {
      ...removeErrors(client),
      ...correctionsWithoutErrors,
    },
    remainingErrors,
  }
}

const manTitle = /(?:^|\s)(?:mr|m\.?|m|monsieur)(?=\s|$)/i

const womanTitle = /(?:^|[^\p{L}])(?:mme\.?|madame)(?=$|[^\p{L}])/iu

export const getTitleInName = (name: string): { title: ETitleClient; cleanedName: string } | null => {
  if (!name) return null

  const matchMan = name.match(manTitle)
  const matchWoman = name.match(womanTitle)

  if (matchMan && matchWoman) {
    return {
      title: ETitleClient['Monsieur et madame'],
      cleanedName: name
        .replace(matchMan[0], ' ')
        .replace(matchWoman[0], ' ')
        .replace(/\s+(?:et|&)\s+/i, '/')
        .replace(/\s+/g, ' ')
        .trim(),
    }
  }

  if (matchWoman) {
    return {
      title: ETitleClient.Madame,
      cleanedName: name.replace(matchWoman[0], '').trim(),
    }
  }

  if (matchMan) {
    return {
      title: ETitleClient.Monsieur,
      cleanedName: name.replace(matchMan[0], '').trim(),
    }
  }

  return null
}
